import { useEffect, useMemo, useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { BrandIcon, TAGLINE } from "@/components/Brand";
import { Apple, Smartphone, Download as DownloadIcon, QrCode, CheckCircle2 } from "lucide-react";

const APP_STORE_URL = "#"; // Coming soon — swap with live App Store listing
const PLAY_STORE_URL = "#"; // Coming soon — swap with live Google Play listing

function detectOS() {
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

const StoreBadge = ({ href, icon: Icon, line1, line2, testid }) => (
  <a
    href={href}
    data-testid={testid}
    className="group inline-flex items-center gap-3 px-5 py-3.5 rounded-2xl border border-white/[0.1] bg-[#1F242D] hover:border-[#F43F5E]/40 hover:-translate-y-0.5 transition-all"
  >
    <Icon className="w-7 h-7 text-white" />
    <span className="text-left leading-tight">
      <span className="block text-[10px] uppercase tracking-wide text-slate-400">{line1}</span>
      <span className="block text-sm font-semibold text-white">{line2}</span>
    </span>
  </a>
);

export default function Download() {
  const [os, setOs] = useState("desktop");
  useEffect(() => { setOs(detectOS()); }, []);

  const qrSrc = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&bgcolor=1F242D&color=FFFFFF&data=${encodeURIComponent(
      `${window.location.origin}/download`
    )}`,
    []
  );

  const primaryHref = os === "ios" ? APP_STORE_URL : os === "android" ? PLAY_STORE_URL : "#";

  return (
    <PublicLayout>
      <section className="relative overflow-hidden spark-grid-bg">
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[360px] rounded-full spark-glow-pulse"
          style={{ background: "radial-gradient(circle, rgba(225,29,72,0.18), transparent 70%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
          <BrandIcon className="h-16 w-auto mx-auto" />
          <h1
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white"
            data-testid="download-title"
          >
            Download <span className="spark-gradient-text">Sparkd</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-xl mx-auto">{TAGLINE}</p>

          {/* Primary CTA */}
          <div className="mt-10">
            <a
              href={primaryHref}
              data-testid="download-primary-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-semibold text-white spark-gradient spark-gradient-hover transition-all active:scale-95"
            >
              <DownloadIcon className="w-5 h-5" />
              {os === "ios" ? "Download on the App Store" : os === "android" ? "Get it on Google Play" : "Download Sparkd"}
            </a>
            <p className="mt-3 text-xs text-slate-500">
              {os === "desktop"
                ? "Scan the code or pick your platform below."
                : "Launching soon — tap to be taken to the store listing."}
            </p>
          </div>

          {/* Desktop: side-by-side badges + QR */}
          {os === "desktop" && (
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="flex flex-col gap-4">
                <StoreBadge href={APP_STORE_URL} icon={Apple} line1="Coming soon on the" line2="App Store" testid="badge-ios" />
                <StoreBadge href={PLAY_STORE_URL} icon={Smartphone} line1="Coming soon on" line2="Google Play" testid="badge-android" />
              </div>
              <div className="rounded-2xl border border-white/[0.08] bg-[#1F242D] p-4" data-testid="download-qr">
                <img src={qrSrc} alt="Scan to open Sparkd on mobile" className="w-[180px] h-[180px] rounded-xl" />
                <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <QrCode className="w-3.5 h-3.5" /> Scan with your phone
                </p>
              </div>
            </div>
          )}

          <div className="mt-14 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400">
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Free to download</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> iOS &amp; Android</span>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
